<template>
    <div class="boq-approval-setup-page">
        <button type="button" class="boq-approval-setup-back" @click="BackToItem">
            <i class="fa-solid fa-chevron-left"></i>
            <span>{{ $t('Back') }}</span>
        </button>

        <div class="boq-approval-setup-titlebar">
            <h1>{{$t('Approval Module.Setup')}} {{isExternal ? $t('Approval Module.External') : $t('Approval Module.Internal')}} {{ $t('Approval Module.Approval') }}</h1>
            <div class="boq-approval-setup-actions">
                <button class="btn boq-approval-setup-btn boq-approval-setup-btn--cancel" @click="BackToItem"><i class="fa-solid fa-xmark"></i> {{$t('Cancel')}}</button>
                <button class="btn boq-approval-setup-btn boq-approval-setup-btn--primary" @click="SendForApproval" :disabled="isSending"><i class="fa-solid fa-envelope"></i> {{sendButtonName}}</button>
            </div>
        </div>

        <div class="boq-approval-setup-summary-grid">
            <section class="boq-approval-card boq-approval-card--details">
                <h2>{{boqId ? $t('Bill Of Quantity') : $t('Quotation')}} Details</h2>
                <div class="boq-approval-card-field">
                    <label>{{boqId ? $t('Bill Of Quantity') : $t('Quotation')}}</label>
                    <div class="boq-approval-display-field">{{ workflowData.itemName || '-' }}</div>
                </div>
            </section>

            <section class="boq-approval-card boq-approval-card--project">
                <h2>Associated Project</h2>
                <div class="boq-approval-card-field">
                    <label>{{ $t('Project') }}</label>
                    <div class="boq-approval-display-field">{{ workflowData.projectName || '-' }}</div>
                </div>
            </section>
        </div>

        <section class="boq-approval-card boq-approval-card--content">
            <h2>Content</h2>
            <div class="boq-approval-content-grid">
                <div class="boq-approval-setup-field">
                    <label class="required-style">{{$t('Approval Module.Date End')}}</label>
                    <DatePicker v-model="dateEnd" elemId="boqreview-date-dateEnd" />
                    <small class="boq-approval-setup-note"><i class="fa-solid fa-circle-info"></i> {{$t('Approval Module.This date is only for a soft deadline this will not affect the approval process')}}.</small>
                </div>

                <div class="boq-approval-setup-field boq-approval-setup-field--message">
                    <label class="required-style">{{$t('Approval Module.Message')}}</label>
                    <RichTextEditor v-model:modelValue="emailMessage" :placeHolder="'Email Message'" :height="'200'" />
                </div>
            </div>
        </section>

        <section class="boq-approval-card boq-approval-card--team boq-approval-reviewers">
            <div class="boq-approval-card-heading">
                <h2>Project Team</h2>
                <label v-if="WillAllowAddExtenalApproves" class="boq-approval-setup-check" for="setup-checkbox-userwide">
                    <input type="checkbox" id="setup-checkbox-userwide" v-model="setupUserWide" />
                    <span>{{isExternal ? 'Search From Master List' : $t('Approval Module.Search Organization Wide')}}</span>
                </label>
            </div>
            <div v-if="WillAllowAddExtenalApproves" class="boq-approval-reviewers__search">
                <div v-if="!isExternal" class="boq-approval-search-card">
                    <div class="boq-approval-team-search-row">
                        <span class="boq-approval-team-search-label">{{$t('Approval Module.Project Team Search')}}</span>
                        <div class="boq-approval-search-control">
                            <i class="fa-solid fa-magnifying-glass"></i>
                            <Select2
                                style="width:100%"
                                indexValue="_id"
                                labelName="fullname"
                                :placeHolder="$t('Search Here')"
                                v-model:optionValue="newTypeName"
                                :onOptionClick="SelectApprover"
                                :filterQuery="SearchMMoserUserFromProjectOrOrganization"
                                ref="select2Approver"
                            >
                            </Select2>
                        </div>
                        <button
                            type="button"
                            class="btn boq-approval-add-btn"
                            @click="AddSelectedApprover"
                            :disabled="!CanAddSelectedApprover"
                        >
                            <span>{{ $t('Add') }}</span>
                            <i class="fa-solid fa-plus"></i>
                        </button>
                    </div>
                </div>
                <div v-else class="boq-approval-search-card">
                    <div class="boq-approval-team-search-row">
                        <span class="boq-approval-team-search-label">External Users Search</span>
                        <div class="boq-approval-search-control">
                            <i class="fa-solid fa-magnifying-glass"></i>
                            <Select2
                                style="width:100%"
                                indexValue="_id"
                                labelName="fullname"
                                placeHolder="Type name here"
                                v-model:optionValue="newTypeName"
                                :onOptionClick="SelectApprover"
                                :filterQuery="SearchExternalUserFromProjectOrOrganization"
                                ref="select2Approver"
                            >
                            </Select2>
                        </div>
                        <button
                            type="button"
                            class="btn boq-approval-add-btn"
                            @click="AddSelectedApprover"
                            :disabled="!CanAddSelectedApprover"
                        >
                            <span>{{ $t('Add') }}</span>
                            <i class="fa-solid fa-plus"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="table-responsive boq-approval-table-wrap">
                <table class="table boq-approval-table">
                    <thead>
                        <tr>
                            <th>{{$t('Approval Module.Approver/Reviewer') }}</th>
                            <th>{{$t('Role') }}</th>
                            <th>{{$t('Status')}}</th>
                            <th v-if="!isExternal || boqId">{{$t('Actions') }}</th>
                        </tr>
                    </thead>
                    <tbody v-if="approversList.length==0">
                        <tr>
                            <td :colspan="(!isExternal || boqId) ? 4 : 3" class="text-center boq-approval-table__empty">
                                <i class="fa-solid fa-user-plus"></i>
                                <span>{{$t('No data Found')}}</span>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-for="(item,key) in approversList" :key="key">
                            <td>
                                <b class="text-capitalize">{{item.fullname}}</b>
                            </td>
                            <td>{{item.roleId.roleName}}</td>
                            <td>
                                <select class="form-select boq-approval-action-select" v-model.number="item.actionType" @change="HandleApproverActionChange(item)" v-if="!isExternal">
                                    <option :value="1">{{ $t('Approval Module.Review') }}</option>
                                    <option :value="2">{{ $t('Approval Module.Approve') }}</option>
                                </select>
                                <span class="boq-approval-role-pill" v-else>
                                    {{ $t('Approval Module.Approve') }}
                                </span>
                            </td>
                            <td v-if="!isExternal || boqId">
                                <button class="btn boq-approval-remove-btn" @click="RemoveApprover(item._id)">{{ $t('Remove') }}</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    </div>
</template>
<script>
import {ForWorkFlowSetup, ForApprovalQuotation, ForWorkFlowSetupExternal, ForApprovalBoq, GetNewBoqCreatorSections} from '@/actions/boqActions.js'
import RichTextEditor from '@/components/layout/RichTextEditor.vue'
import {SearchMMoserUserFromProjectOrOrganization, UserSendEmailBulk, SearchExternalUserFromProjectOrOrganization} from '@/actions/projectActions.js'
import {CheckSessionMsal} from '@/actions/userActions.js'
import Select2  from '@/components/layout/Select2.vue'
import  {mapActions, mapGetters} from 'vuex'
import {LinkList, ReformatDate,TextCapitalize} from '@/helpers/utilities.js'
import 'dotenv/config'
import DatePicker from '@/components/layout/DatePicker.vue'
import {LocaleNameVariable} from '@/translations/i18n.js'
export default {
    name : 'BoqReviewerSetup',
    data(){

        const d = new Date()
        d.setDate(d.getDate() + 7)
        const self = this

        return {
            quotationId : this.$route.query.qtnId,
            isExternal : this.$route.query.isExternal,
            boqId : this.$route.query.boqId,
            workflowData : {
                projectName : '-',
                itemName : '-',
                workflow : []
            },
            approversList :[],
            emailMessage : '',
            newTypeName : '',
            selectedApprover : null,
            isSending :false,
            sendButtonName : self.$i18n.t('Send For Approval'),
            setupUserWide : false,
            isSession : false,
            dateEnd : ReformatDate(d,'YYYY-MM-DD'),
            presetDate : ReformatDate(d),
            dotnetUrl : process.env.VUE_APP_DOTNET_URL,
            
        }
    },
    computed: {
        ...mapGetters(['projectInfo']),
        WillAllowAddExtenalApproves(){
            const self = this
            if(!self.isExternal){
                return true
            }

            return self.boqId
        },
        CanAddSelectedApprover(){
            const self = this
            if(!self.selectedApprover){
                return false
            }

            if(self.approversList.findIndex(u=>u._id==self.selectedApprover._id)>-1){
                return false
            }

            return true
        }
    },
    mounted : async function (){
        const self = this
        let type = self.quotationId ? 3 : 1

        const checkEmailSession = await CheckSessionMsal(type)
        self.isSession = checkEmailSession
        if(!checkEmailSession){
            if(self.quotationId){
                window.location.href = process.env.VUE_APP_REST_MAIN_API + '/api/auth/token/email?type=' + type+'&returnUrl='+encodeURIComponent(window.location.href)
            }else{
                window.location.href = process.env.VUE_APP_REST_MAIN_API + '/api/auth/token/email?type=' + type+'&returnUrl='+encodeURIComponent(window.location.href)
            }
        }

        self.workflowData = await ForWorkFlowSetup({qtnId : self.quotationId, boqId : self.boqId, isExternal : self.isExternal })

        if(self.workflowData.workflow.length!=0){
            self.workflowData.workflow.forEach((value, index) => {
                self.approversList.push({
                    _id : value._id,
                    fullname : value.fullname,
                    roleId :{
                        roleName : value.roleId?.roleName || ''
                    }, 
                    actionType : self.isExternal || index==0 ? 2 : 1,
                })
            })
            self.EnsureSingleApprover()
        }
        await self.GetProjectInfo(self.workflowData.projectId)

        let nameOfItem = ''
        if(self.quotationId){
            nameOfItem = `${self.$i18n.t('Quotation')} <b>${TextCapitalize(self.workflowData.itemName)}</b> `
            self.ChangeActiveLink(LinkList.QUOTATIONS)
        }else if(self.boqId){
            nameOfItem = `${self.$i18n.t('Bill Of Quantity')} <b>${TextCapitalize(self.workflowData.itemName)}</b>`
            self.ChangeActiveLink(LinkList.BILL_OF_QUANTITIES)

        }
        const getLanguage = window.localStorage.getItem(LocaleNameVariable) || 'en'

        
        if(getLanguage=='en' && self.boqId)
        {
            self.emailMessage = `
            <style>
                h1, h2, h3, h4, h5, h6 {
                    text-align: left !important;
                    margin-left: 0 !important;
                    margin-right: auto !important;
                }
            </style>
            <div style="text-align:left; font-family:Arial, sans-serif; font-size:14px; line-height:1.5; color:#000000;">
                <p style="margin:0 0 22px 0;">Hi,</p>
                <p style="margin:0 0 22px 0;">
                    We hope this message finds you well. Bill of Quantity <b>${TextCapitalize(self.workflowData.itemName)}</b> for project <b>${this.workflowData.projectName}</b> is ready for your review and approval. Your approval is required by <b>${ReformatDate(self.dateEnd)}</b>.
                </p>
                <p style="margin:0;">
                    If you have any questions or concerns, please do not hesitate to reach out.
                </p>
            </div>
            `
        }
        else if(getLanguage=='en')
        {
            self.emailMessage = `
            We hope this message finds you well. ${nameOfItem} for the project <b>${this.workflowData.projectName}</b> is now ready for your review and approval. Your approval is required by <b>${ReformatDate(self.dateEnd)}</b>. 
            <br/><br/>
            Please note that timely approval is vital to maintaining our project's momentum and avoiding potential delays. We appreciate your cooperation and await your positive response by <b>${ReformatDate(self.dateEnd)}</b>.
            <br/><br/>
            Please let us know if you have any questions or concerns. We would be happy to provide any additional information you may need. Once we receive your approval, we will proceed with the project as outlined. Thank you for your time and consideration.
            `
        }
        else
        {
            self.emailMessage = `
            希望此消息能找到您。${nameOfItem} 项目 <b>${this.workflowData.projectName}</b> 现已准备好供您审核和批准。您的批准需要在 <b>${ReformatDate(self.dateEnd)}</b> 之前完成。
            <br/><br/>
            请注意，及时批准对维持我们项目的进度和避免潜在的延误至关重要。我们感谢您的合作，并期待您在 <b>${ReformatDate(self.dateEnd)}</b> 之前给予积极回应。
            <br/><br/>
            如果您有任何问题或疑虑，请告诉我们。我们很乐意提供您可能需要的任何额外信息。一旦收到您的批准，我们将按照计划继续项目。感谢您的时间和考虑。
            `
        }
    },
    components : {RichTextEditor, Select2, DatePicker},
    watch :{
        dateEnd(val){
            const self = this
            self.emailMessage = self.emailMessage.replaceAll(self.presetDate, ReformatDate(val))
            self.presetDate = ReformatDate(val)
        },  
    },
    methods : {
        
        ...mapActions(['GetProjectInfo','ChangeActiveLink']),
        SearchExternalUserFromProjectOrOrganization (value){
            return SearchExternalUserFromProjectOrOrganization(value,this.workflowData.projectId, this.setupUserWide ? 1 : 0)
        },
        SearchMMoserUserFromProjectOrOrganization(search){
            return SearchMMoserUserFromProjectOrOrganization(search, this.workflowData.projectId, this.setupUserWide ? 1 : 0)
        },
        async HasBoqSections(){
            const sectionData = await GetNewBoqCreatorSections(this.boqId).catch(()=>null)
            return Array.isArray(sectionData?.sections) && sectionData.sections.length > 0
        },
        async SendForApproval(){
            const self = this
            if(self.isSending){
                return
            }
            const swalTitleSendForApproval = self.$i18n.t('Send For Approval')
            const swalTitleSendEmail = self.$i18n.t('Approval Module.Sending Email')

            if(self.boqId && !(await self.HasBoqSections())){
                self.$swal(
                    swalTitleSendForApproval,
                    self.$i18n.t('Please add at least one section before sending this Bill of Quantity for approval.'),
                    'warning'
                )
                return
            }

            if(self.approversList.length==0){
                self.$swal(swalTitleSendForApproval,self.$i18n.t('Approval Module.Please put atleast one Approver/Reviewer'),'warning')
                return
            }

            if(!self.HasApprover()){
                self.$swal(swalTitleSendForApproval,'Please keep one user as Approve before sending. The remaining users can stay as Review.','warning')
                return
            }

            if(!self.dateEnd){
                
                return
            }
            let appendDateMessage = self.emailMessage
            const emailSubject = self.boqId
                ? `${self.workflowData.projectName} - Approval of Bill of Quantity`
                : `Project : ${self.workflowData.projectName} -  ${self.workflowData.itemName}`

            self.isSending = true
            if(self.isExternal){


                ForWorkFlowSetupExternal({quotationId : self.quotationId,boqId : self.boqId, approverList : self.approversList,dateEnd : self.dateEnd }).then((res)=>{
                    if(!res.processId){
                        self.sendButtonName = swalTitleSendForApproval
                        self.$swal(swalTitleSendForApproval,res,'warning')
                    }else{
                        self.sendButtonName = swalTitleSendEmail + '...'
                        
                        let urlLink = ''
                        if(self.quotationId){
                            urlLink = `${self.dotnetUrl}/client/project/${self.projectInfo.projectOldId}/quotation/${res.processId}/approval`
                        }else if(self.boqId){
                            urlLink = `${self.dotnetUrl}/client/project/${self.projectInfo.projectOldId}/boq/${res.processId}/approval`
                        }
                        UserSendEmailBulk({
                            subject : emailSubject,
                            message : appendDateMessage,
                            link : urlLink,
                            sendTos : self.approversList
                        }).then((resEmail)=>{
                            if(resEmail!='OK'){
                                self.isSending = false
                                self.sendButtonName = swalTitleSendForApproval
                                self.$swal(swalTitleSendForApproval,'Email Sending Failed','warning')
                            }else{
                                if(self.quotationId){
                                    self.$router.push(`/boq/${self.quotationId}/quotation/item`)
                                }
                                else if(self.boqId){
                                    self.$router.push({name : 'BoqBuilder', params : { id : self.boqId} })
                                }
                            }
                        })
                    }

                })

            }else{
                self.sendButtonName = swalTitleSendEmail + '...'
                if(self.quotationId){
                    ForApprovalQuotation({
                        quotationId : self.quotationId,
                        approverList : self.approversList,
                        isExternal : self.isExternal,
                        dateEnd : self.dateEnd
                    }).then((res)=>{
                        if(!res.processId){
                            self.isSending = false
                            self.sendButtonName = swalTitleSendForApproval
                            self.$swal(swalTitleSendForApproval,res,'warning')
                        }else{
                            self.sendButtonName = swalTitleSendEmail + '...'
                            UserSendEmailBulk({
                                subject : emailSubject,
                                message : appendDateMessage,
                                link : `${window.location.origin}/boq/${res.processId}/quotation/approval`,
                                sendTos : self.approversList
                            }).then((resEmail)=>{
                                if(resEmail!='OK'){
                                    self.isSending = false
                                    self.sendButtonName = swalTitleSendForApproval
                                    self.$swal(swalTitleSendForApproval,'Email Sending Failed','warning')
                                }else{
                                    self.$router.push(`/boq/${self.quotationId}/quotation/item`)
                                }
                            })
                        }

                    })
                }else if(self.boqId){
                    ForApprovalBoq({
                        boqId : self.boqId,
                        approverList : self.approversList,
                        dateEnd : self.dateEnd
                    }).then((res)=>{
                        if(!res.processId){
                            self.isSending = false
                            self.sendButtonName = swalTitleSendForApproval
                            self.$swal(swalTitleSendForApproval,res,'warning')
                        }else{
                            self.sendButtonName = swalTitleSendEmail + '...'
                            UserSendEmailBulk({
                                subject : emailSubject,
                                message : appendDateMessage,
                                link : `${window.location.origin}/boq/Review/${res.processId}`,
                                sendTos : self.approversList
                            }).then((resEmail)=>{
                                if(resEmail!='OK'){
                                    self.isSending = false
                                    self.sendButtonName = swalTitleSendForApproval
                                    self.$swal(swalTitleSendForApproval,self.$i18n.t('Approval Module.Email Sending Failed'),'warning')
                                }else{
                                    self.$router.push({name : 'BoqBuilder', params : { id : self.boqId} })
                                }
                            })
                        }

                    })
                }
            }
            
        },
        SelectApprover(value){
            this.selectedApprover = value
        },
        HasApprover(){
            return this.approversList.some(u=>Number(u.actionType)==2)
        },
        GetDefaultActionType(){
            if(this.isExternal){
                return 2
            }

            return this.HasApprover() ? 1 : 2
        },
        EnsureSingleApprover(preferredId){
            const self = this
            if(self.isExternal || self.approversList.length==0){
                return
            }

            let approverIndex = preferredId ? self.approversList.findIndex(u=>u._id==preferredId) : self.approversList.findIndex(u=>Number(u.actionType)==2)
            if(approverIndex==-1){
                approverIndex = 0
            }

            self.approversList.forEach((u,index)=> {
                u.actionType = index==approverIndex ? 2 : 1
            })
        },
        HandleApproverActionChange(item){
            item.actionType = Number(item.actionType)
            if(item.actionType==2){
                this.EnsureSingleApprover(item._id)
                return
            }

            this.EnsureSingleApprover()
        },
        AddSelectedApprover(){
            const self = this
            if(!self.CanAddSelectedApprover){
                return
            }

            self.approversList.push({
                _id : self.selectedApprover._id,
                fullname : self.selectedApprover.fullname,
                roleId :{
                    roleName : self.selectedApprover.roleId?.roleName || ''
                }, 
                actionType : self.GetDefaultActionType(),
            })
            self.EnsureSingleApprover()

            setTimeout(function(){
                self.newTypeName = ''
                self.selectedApprover = null
            },300)
        },
        AddApprover(value){
            this.SelectApprover(value)
            this.AddSelectedApprover()
        },
        AddApproverExternal(value){
            this.SelectApprover(value)
            this.AddSelectedApprover()
        },
        RemoveApprover(id){
            const self = this
            const getIndex = self.approversList.findIndex(u=>u._id==id)
            if(getIndex>-1){
                self.approversList.splice(getIndex,1)
                self.EnsureSingleApprover()
            }
        },
        BackToItem(){
            if(this.quotationId){
                this.$router.push(`/boq/${this.quotationId}/quotation/item`)
            }else{
                this.$router.push({name : 'BoqBuilder', params : { id : this.boqId} })
            }
        }
    }
}
</script>
